import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import {
  queryChangeHistory,
  getChangeEventDetails,
  getTrackedTables,
  type ChangeHistoryQueryParams,
} from '../api/changeHistory';
import { resolveUserIds } from '../api/auditoria';
import { queryKeys } from '@/lib/pwa/queryConfig';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function useChangeHistoryQuery(
  params: ChangeHistoryQueryParams,
  enabled = true,
  refetchInterval: number | false = false,
) {
  return useQuery({
    queryKey: [...queryKeys.admin.changeHistory, params],
    queryFn: () => queryChangeHistory(params),
    enabled,
    refetchInterval,
  });
}

export function useChangeEventDetails(eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.admin.changeHistory, 'details', eventId],
    queryFn: () => getChangeEventDetails(eventId),
    enabled: enabled && !!eventId,
    staleTime: 5 * 60_000,
  });
}

export function useTrackedTables() {
  return useQuery({
    queryKey: queryKeys.admin.trackedTables,
    queryFn: getTrackedTables,
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
  });
}

/**
 * Resolve actor_ids to user names, filtering out non-UUID values.
 * Non-UUID actor_ids (empty, worker IDs) are not sent to the resolver.
 */
export function useChangeActorNames(actorIds: (string | null)[]) {
  const uuidIds = useMemo(
    () => [...new Set(actorIds.filter((id): id is string => !!id && UUID_RE.test(id)))].sort(),
    [actorIds],
  );
  return useQuery({
    queryKey: [...queryKeys.admin.userNames, 'change-actors', uuidIds.join(',')],
    queryFn: () => resolveUserIds(uuidIds),
    enabled: uuidIds.length > 0,
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
  });
}

/**
 * Returns display name for an actor_id.
 * UUID actors are resolved to user names; non-UUID show as "Sistema".
 */
export function getActorDisplayName(
  actorId: string | null,
  resolved?: Record<string, string>,
): string | undefined {
  if (!actorId) return undefined;
  if (!UUID_RE.test(actorId)) return actorId || 'Sistema';
  return resolved?.[actorId];
}
